/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';
import URI from 'jsuri';
import { every } from 'lodash';

import config from 'js/app/config';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';
import useUrlSessionStore from 'js/lib/useUrlSessionStore';

import { Grid, TextField, Typography } from '@coursera/cds-core';
// Components
import { Radio, RadioGroup } from '@coursera/coursera-ui';
import { email as isEmailInvalid } from '@coursera/coursera-ui/lib/utils/validation';

import useFormField from 'bundles/enterprise-admin-account/utils/useFormField';
import type { ValidateFunc } from 'bundles/enterprise-admin-account/utils/useFormField';
import { TrackedButton } from 'bundles/enterprise-ui';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import DiscountPill from 'bundles/teams-v2/components/DiscountPill';
import HeadingWithSocialProofPill from 'bundles/teams-v2/components/HeadingWithSocialProofPill';
import InvalidPromotionBanner from 'bundles/teams-v2/components/InvalidPromotionBanner';
import TeamsCheckoutWarnings from 'bundles/teams-v2/components/TeamsCheckoutWarnings';
import { TeamsContext } from 'bundles/teams-v2/components/TeamsContainer';
import type { CheckoutSessionType } from 'bundles/teams-v2/components/TeamsMainApp';
// Utils
import { isCheckingOutForExistingOrg, updateRouterParam } from 'bundles/teams-v2/components/utils';
import { ANNUALLY, LOWER_LEARNER_LIMIT, QUARTERLY, UPPER_LEARNER_LIMIT } from 'bundles/teams-v2/constants/billing';
import type { WithDiscretionaryDiscountProps } from 'bundles/teams-v2/utils/withDiscretionaryDiscount';

// i18n
import _t from 'i18n!nls/teams-v2';

import 'css!./__styles__/PlanDetails';

export type Props = WithDiscretionaryDiscountProps;

const CONTACT_SALES_URL = `${config.url.base}business/learn-more`;

export const PlanDetails: React.FC<{}> = () => {
  const { sessionStore, pushWithSession } = useUrlSessionStore<CheckoutSessionType>();
  const trackComponent = useRetracked();
  const router = useRouter();

  const teamsContext = React.useContext(TeamsContext);
  const { promotion, enterprisePromotion, isPromotionEligible, discretionaryDiscount } = teamsContext;

  const [isSalesLinkVisible, setIsSalesLinkVisible] = React.useState(false);

  const validateLearnerCount: ValidateFunc<number | null> = (value, success, error) => {
    if (!value || value < LOWER_LEARNER_LIMIT) {
      return error(_t('Minimum #{lowerLimit} users.', { lowerLimit: LOWER_LEARNER_LIMIT }));
    } else if (value > UPPER_LEARNER_LIMIT) {
      setIsSalesLinkVisible(true);
      return error(_t('Maximum #{upperLimit} users.', { upperLimit: UPPER_LEARNER_LIMIT }));
    } else {
      return success();
    }
  };

  const learnerCount = useFormField(
    // lazy init initial value
    teamsContext.learnerCount ?? null,
    // validation
    validateLearnerCount
  );

  const billingType = useFormField(teamsContext.billingType ?? ANNUALLY);

  const onBillingTypeChange = ({ target: { checked, value } }: React.ChangeEvent<HTMLInputElement>) => {
    if (checked) {
      const billingTypeValue = parseInt(value, 10);
      billingType.setValue(billingTypeValue);
      updateRouterParam(router, '/purchase/plan', { billingType: billingTypeValue });

      trackComponent({ trackingData: {}, trackingName: 'plan_details_billing_type', action: 'change' });
    }
  };

  const handleLearnerCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const num = parseInt(value, 10);
    learnerCount.setValue(isNaN(num) ? null : num);
    setIsSalesLinkVisible(false);
  };

  const handleLearnerCountFocus = () => {
    trackComponent({ trackingData: {}, trackingName: 'plan_details_learner_count', action: 'focus' });
  };

  const handleLearnerCountBlur = () => {
    trackComponent({ trackingData: {}, trackingName: 'plan_details_learner_count', action: 'blur' });
    updateRouterParam(router, '/purchase/plan', { learnerCount: learnerCount.value ?? '' });
  };

  const isAlreadyC4TUser = (email: string) => {
    const { alreadyC4TUserEmail } = sessionStore.getState();
    return email === alreadyC4TUserEmail;
  };

  const validateBusinessEmail: ValidateFunc<string | null> = (businessEmail, success, error) => {
    if (!businessEmail || isEmailInvalid(businessEmail)) {
      return error(_t('Please enter a valid email.'));
    } else {
      return success();
    }
  };

  const validateInputs = () => {
    // skip validating learner input since it is fixed

    const v: Record<string, boolean> = {
      learnerCountValid: learnerCount.validate(),
      businessEmailValid: true,
    };

    return every([v.learnerCountValid, v.businessEmailValid]);
  };

  const handleContinue = () => {
    if (!validateInputs()) {
      return;
    }

    const query = router && router.location && router.location.query;
    const newQuery = {
      ...query,
      billingType: billingType.value,
      learnerCount: learnerCount.value,
      role: sessionStore.getState().role,
      organizationType: sessionStore.getState().organizationType,
      organizationName: sessionStore.getState().organizationName,
      organizationSizeCategory: sessionStore.getState().organizationSizeCategory,
    };

    let pathname;
    if (isCheckingOutForExistingOrg(router)) {
      pathname = '/purchase/billing';
    } else {
      pathname = '/purchase/account';
    }

    pushWithSession({ pathname, query: newQuery });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleContinue();
  };

  const navigateToNonTrial = () => {
    // An HREF is used for this functionality for a couple reasons:
    // 1. We want this route change to be "backable" similar to route.push
    // 2. We want the element to be a <a> tag for a11y. A button isn't really appropriate
    const uri = new URI(router.location.pathname);
    return uri.toString();
  };

  React.useEffect(
    () => {
      // skip validating learner input since it is fixed
      if (learnerCount.value) {
        learnerCount.validate();
      }

      // Setup route params if initial values exist
      const query: Record<string, string | number> = {};
      if (learnerCount.value) {
        query.learnerCount = learnerCount.value;
      }

      if (billingType.value) {
        query.billingType = billingType.value;
      }

      updateRouterParam(router, '/purchase/plan', query);
    }, // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  React.useEffect(() => {
    // DD is loaded in async to allow failure (required: false)
    // This means we need to set the value manually when the DD is loaded
    if (discretionaryDiscount?.licenseCount) {
      learnerCount.setValue(discretionaryDiscount.licenseCount);
    }
  }, [learnerCount, discretionaryDiscount]);

  const billingIsAnnual = billingType.value === ANNUALLY;
  const hasPromotion = !!(promotion && enterprisePromotion);

  let supportText: string | undefined;

  if (discretionaryDiscount) {
    supportText = _t('This offer is limited to #{numberOfLicenses} users, but you can add more later!', {
      numberOfLicenses: discretionaryDiscount.licenseCount,
    });
  }

  return (
    <form className="rc-PlanDetails rc-container" onSubmit={handleSubmit}>
      <Grid container spacing={24}>
        <Grid item xs={12}>
          <HeadingWithSocialProofPill showSocialProofPill={!(hasPromotion && isPromotionEligible)}>
            <Typography variant="h1">{_t('Configure your subscription')}</Typography>
          </HeadingWithSocialProofPill>
        </Grid>

        <Grid item xs={12}>
          <TeamsCheckoutWarnings />
        </Grid>
        {hasPromotion && promotion && !isPromotionEligible && (
          <Grid item xs={12}>
            <InvalidPromotionBanner promotion={promotion} />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            label={_t('Number of users')}
            supportText={supportText}
            placeholder={_t('eg. 10')}
            fullWidth
            value={learnerCount.value ? String(learnerCount.value) : ''}
            validationStatus={learnerCount.validationStatus}
            validationLabel={learnerCount.validationLabel}
            onChange={handleLearnerCountChange}
            onFocus={handleLearnerCountFocus}
            onBlur={handleLearnerCountBlur}
            disabled={!!discretionaryDiscount}
            data-e2e="learner-count-input"
          />
        </Grid>
        <Grid item xs={12}>
          <label htmlFor="billingType" className="label-text">
            {hasPromotion ? _t('How would you like to pay?') : _t('Payment Option')}
          </label>
          <RadioGroup
            rootClassName="c-radioGroup horizontal-box"
            onChange={onBillingTypeChange}
            value={billingType.value}
          >
            <span className={classNames('c-radioItem', { selected: billingIsAnnual })}>
              <Radio readOnly={billingIsAnnual} value={ANNUALLY}>
                <div className="vertical-box">
                  <p className="c-radioText">{_t('Annual')}</p>
                  <p className="c-radioText mobile-smaller-text">{_t('Billed once a year')}</p>
                </div>
              </Radio>
            </span>
            <span className={classNames('c-radioItem', { selected: !billingIsAnnual })}>
              <Radio readOnly={!billingIsAnnual} value={QUARTERLY}>
                <div className="vertical-box">
                  <p className="c-radioText">{_t('Quarterly')}</p>
                  <p className="c-radioText mobile-smaller-text">{_t('Billed 4 times a year')}</p>
                </div>
              </Radio>
            </span>
          </RadioGroup>
        </Grid>

        {promotion && hasPromotion && isPromotionEligible && (
          <Grid item xs={12}>
            <DiscountPill discountPercent={promotion.discountPercent} promotionName={promotion.promotionName} />
          </Grid>
        )}

        {isSalesLinkVisible && (
          <Grid item xs={12}>
            <FormattedMessage
              message={_t('Please {contactSales} if you have over {upperLimit} users.')}
              upperLimit={UPPER_LEARNER_LIMIT}
              contactSales={
                <TrackedA
                  trackingName="contact_sales_link"
                  href={CONTACT_SALES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {_t('contact sales')}
                </TrackedA>
              }
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TrackedButton
            trackingName="plan_details_continue_button"
            variant="primary"
            data-e2e="plan-details-continue-button"
            onClick={handleContinue}
          >
            {_t('Continue')}
          </TrackedButton>
        </Grid>
      </Grid>
    </form>
  );
};

export default PlanDetails;
